import { useState, useCallback, useMemo } from "react";
import type { CalculationElement, VariableType } from "../types";
import type { NodeVariable } from "../../../types";
import type { Operation } from "../utils/operations";

/**
 * Hook principal para gestionar el estado del campo calculado
 */
export const useCalculatedField = (variables: NodeVariable[]) => {
  const [selectedVariable, setSelectedVariable] = useState<string>();
  const [selectedOperation, setSelectedOperation] = useState<Operation>();
  const [elements, setElements] = useState<CalculationElement[]>([]);
  const [selectedDateGetFormat, setSelectedDateGetFormat] = useState<string>();
  const [selectedDateToStringFormat, setSelectedDateToStringFormat] =
    useState<string>();
  const [selectedArrayCondition, setSelectedArrayCondition] =
    useState<string>();
  const [selectedArrayFunction, setSelectedArrayFunction] = useState<string>();

  // Función para agregar un nuevo elemento
  const addElement = useCallback(() => {
    if (elements.length < 10) {
      const newElement: CalculationElement = {
        id: Math.random().toString(36).substr(2, 9),
        type: "inp",
        value: "",
      };
      setElements((prev) => [...prev, newElement]);
    }
  }, [elements.length]);

  // Función para eliminar un elemento
  const removeElement = useCallback((id: string) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
  }, []);

  // Función para actualizar el valor de un elemento
  const updateElement = useCallback((id: string, value: string) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, value } : el)),
    );
  }, []);

  // Función para actualizar propiedades específicas de un elemento
  const updateElementProperty = useCallback(
    (id: string, property: keyof CalculationElement, value: string) => {
      setElements((prev) =>
        prev.map((el) => (el.id === id ? { ...el, [property]: value } : el)),
      );
    },
    [],
  );

  // Handler para cambiar variable (limpia operación)
  const handleVariableChange = useCallback((value: string) => {
    setSelectedVariable(value);
    setSelectedOperation(undefined);
  }, []);

  // Handler especial para deserialización (NO limpia operación)
  const setVariableForDeserialization = useCallback((value: string) => {
    setSelectedVariable(value);
  }, []);

  // Función para limpiar todo el estado
  const clearState = useCallback(() => {
    setSelectedVariable(undefined);
    setSelectedOperation(undefined);
    setElements([]);
    setSelectedDateGetFormat(undefined);
    setSelectedDateToStringFormat(undefined);
    setSelectedArrayCondition(undefined);
    setSelectedArrayFunction(undefined);
  }, []);

  // Obtener tipo de variable seleccionada
  const selectedVariableType: VariableType | undefined = useMemo(() => {
    return variables
      .flatMap((v) => v.variables)
      .find((variable) => variable.id === selectedVariable)?.type;
  }, [selectedVariable, variables]);

  // Obtener meta_type de la variable seleccionada (ej. "date" vs "datetime")
  const selectedVariableMetaType: string | undefined = useMemo(() => {
    return variables
      .flatMap((v) => v.variables)
      .find((variable) => variable.id === selectedVariable)?.meta_type;
  }, [selectedVariable, variables]);

  // Verificar si mostrar elementos dinámicos
  const showDynamicElements = Boolean(
    selectedVariable && selectedOperation?.needsValue,
  );

  const hasMultipleValues = Boolean(selectedOperation?.hasMultipleValues);

  return {
    selectedVariable,
    selectedOperation,
    elements,
    selectedDateGetFormat,
    selectedDateToStringFormat,
    selectedArrayCondition,
    selectedVariableType,
    selectedVariableMetaType,
    showDynamicElements,
    hasMultipleValues,
    selectedArrayFunction,
    setSelectedArrayFunction,
    setSelectedOperation,
    setSelectedDateGetFormat,
    setSelectedDateToStringFormat,
    setSelectedArrayCondition,
    setElements,
    addElement,
    removeElement,
    updateElement,
    updateElementProperty,
    handleVariableChange,
    setVariableForDeserialization,
    clearState,
  };
};
